// src/context/NavigationContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const NavigationContext = createContext();

// 통합된 네비게이션 매핑 데이터
// 각 경로마다 breadcrumbs와 타이틀 정보 포함
const routeConfig = {
  // 대시보드
  '/': {
    title: '대시보드',
    menuTitle: '대시보드',
    breadcrumbs: [
      { label: '대시보드', path: '/' }
    ]
  },
  '/dashboard': {
    title: '대시보드',
    menuTitle: '대시보드',
    breadcrumbs: [
      { label: '대시보드', path: '/dashboard' }
    ]
  },
  
  // 회원 관리
  '/users': {
    title: '회원 조회',
    menuTitle: '회원 관리',
    subMenuLabel: '회원 조회',
    breadcrumbs: [
      { label: '회원 관리', path: '/users' },
      { label: '회원 조회', path: '/users' }
    ]
  },
  '/users/:id': {
    title: '회원 상세',
    menuTitle: '회원 관리',
    subMenuLabel: '회원 조회',
    breadcrumbs: [
      { label: '회원 관리', path: '/users' },
      { label: '회원 조회', path: '/users' },
      { label: '회원 상세', path: '' }
    ],
    detailLabel: id => `회원 상세 정보`
  },
  
  // 공지사항
  '/notices': {
    title: '공지사항 조회',
    menuTitle: '공지사항',
    subMenuLabel: '공지사항 조회',
    breadcrumbs: [
      { label: '공지사항', path: '/notices' },
      { label: '공지사항 조회', path: '/notices' }
    ]
  },
  '/notices/create': {
    title: '공지사항 생성',
    menuTitle: '공지사항',
    subMenuLabel: '공지사항 생성',
    breadcrumbs: [
      { label: '공지사항', path: '/notices' },
      { label: '공지사항 생성', path: '/notices/create' }
    ]
  },
  '/notices/:id': {
    title: '공지사항 상세',
    menuTitle: '공지사항',
    subMenuLabel: '공지사항 조회',
    breadcrumbs: [
      { label: '공지사항', path: '/notices' },
      { label: '공지사항 조회', path: '/notices' },
      { label: '공지사항 상세', path: '' }
    ],
    detailLabel: id => `공지사항 상세 보기`
  },
  '/notices/:id/edit': {
    title: '공지사항 수정',
    menuTitle: '공지사항',
    subMenuLabel: '공지사항 조회',
    breadcrumbs: [
      { label: '공지사항', path: '/notices' },
      { label: '공지사항 조회', path: '/notices' },
      { label: '공지사항 수정', path: '' }
    ],
    detailLabel: id => `공지사항 수정`
  },
  
  // 필터
  '/filters': {
    title: '최신필터 조회',
    menuTitle: '최신 필터',
    subMenuLabel: '최신필터 조회',
    breadcrumbs: [
      { label: '최신 필터', path: '/filters' },
      { label: '최신필터 조회', path: '/filters' }
    ]
  },
  '/filters/create': {
    title: '최신필터 생성',
    menuTitle: '최신 필터',
    subMenuLabel: '최신필터 생성',
    breadcrumbs: [
      { label: '최신 필터', path: '/filters' },
      { label: '최신필터 생성', path: '/filters/create' }
    ]
  },
  '/filters/:id': {
    title: '최신필터 상세',
    menuTitle: '최신 필터',
    subMenuLabel: '최신필터 조회',
    breadcrumbs: [
      { label: '최신 필터', path: '/filters' },
      { label: '최신필터 조회', path: '/filters' },
      { label: '최신필터 상세', path: '' }
    ],
    detailLabel: id => `최신필터 상세 보기`
  },
  '/filters/:id/edit': {
    title: '최신필터 수정',
    menuTitle: '최신 필터',
    subMenuLabel: '최신필터 조회',
    breadcrumbs: [
      { label: '최신 필터', path: '/filters' },
      { label: '최신필터 조회', path: '/filters' },
      { label: '최신필터 수정', path: '' }
    ],
    detailLabel: id => `최신필터 수정`
  },
  '/privacy': {
    title: '개인정보처리방침',
    menuTitle: '개인정보처리방침',
    breadcrumbs: [
      { label: '개인정보처리방침', path: '/privacy' }
    ]
  }
};

export function NavigationProvider({ children }) {
  const location = useLocation();
  
  // 현재 경로에서 패턴 매칭된 기본 경로 추출 
  // 예: /notices/123 → /notices/:id
  const getMatchedBasePath = (path) => {
    const segments = path.split('/');
    
    // 패턴 매칭 시도 - 순서 수정됨
    if (segments.length > 2) {
      // Create 패턴 먼저 확인 (ex: /notices/create -> /notices/create)
      if (segments[2] === 'create') {
        const createPattern = `/${segments[1]}/create`;
        if (routeConfig[createPattern]) {
          return createPattern;
        }
      }
      
      // Edit 패턴 두번째로 확인 (ex: /notices/123/edit -> /notices/:id/edit)
      if (segments.length > 3 && segments[3] === 'edit') {
        const possibleEditPattern = `/${segments[1]}/:id/edit`;
        if (routeConfig[possibleEditPattern]) {
          return possibleEditPattern;
        }
      }
      
      // ID 포함 패턴 마지막으로 확인 (ex: /notices/123 -> /notices/:id)
      const possibleIdPattern = `/${segments[1]}/:id`;
      if (routeConfig[possibleIdPattern]) {
        return possibleIdPattern;
      }
    }
    
    // 기본 경로로 시도
    if (routeConfig[path]) {
      return path;
    }
    
    // 매치되는 것이 없다면 최상위 경로 시도
    return '/';
  };

  // 기본 메뉴와 서브메뉴 상태
  const [activeMenu, setActiveMenu] = useState('회원 관리');
  const [activeSubMenu, setActiveSubMenu] = useState('회원 조회');
  
  // breadcrumb 경로를 관리하는 상태
  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: '회원 관리', path: '/users' },
    { label: '회원 조회', path: '/users' }
  ]);
  
  // 현재 페이지 타이틀
  const [currentTitle, setCurrentTitle] = useState('회원 조회');

  // URL 변경 시 breadcrumb 자동 업데이트
  useEffect(() => {
    const currentPath = location.pathname;
    const matchedPath = getMatchedBasePath(currentPath);
    
    // 해당 경로의 설정 가져오기
    const routeSettings = routeConfig[matchedPath];
    
    if (!routeSettings) {
      // 설정이 없는 경로인 경우 기본값 사용
      setActiveMenu('회원 관리');
      setActiveSubMenu('회원 조회');
      setBreadcrumbs([
        { label: '회원 관리', path: '/users' },
        { label: '회원 조회', path: '/users' }
      ]);
      setCurrentTitle('회원 조회');
      return;
    }
    
    // 기본 breadcrumbs 가져오기
    let currentBreadcrumbs = [...routeSettings.breadcrumbs];
    
    // 경로에 ID가 포함된 경우 (상세보기, 수정 등)
    if (matchedPath.includes(':id') && routeSettings.detailLabel) {
      const pathParts = currentPath.split('/');
      const id = pathParts[2]; // ID 추출
      
      // 마지막 breadcrumb 항목의 경로 업데이트
      const lastBreadcrumb = currentBreadcrumbs[currentBreadcrumbs.length - 1];
      if (!lastBreadcrumb.path) {
        lastBreadcrumb.path = currentPath;
      }
      
      // 상세 항목 레이블 추가
      lastBreadcrumb.label = routeSettings.detailLabel(id);
    }
    
    // Nav 컴포넌트와 일치하도록 activeMenu, activeSubMenu 설정
    setActiveMenu(routeSettings.menuTitle);
    setActiveSubMenu(routeSettings.subMenuLabel || '');
    
    // breadcrumbs 설정
    setBreadcrumbs(currentBreadcrumbs);
    
    // 현재 페이지 타이틀 설정
    setCurrentTitle(routeSettings.title);
    
  }, [location.pathname]);

  // 메뉴 및 breadcrumb 수동 업데이트 함수
  const updateNavigation = (menu, subMenu, path) => {
    setActiveMenu(menu);
    setActiveSubMenu(subMenu);
    
    let newBreadcrumbs = [];
    
    // 경로가 제공되었으면 해당 경로의 기본 breadcrumb 사용
    if (path && routeConfig[path]) {
      newBreadcrumbs = [...routeConfig[path].breadcrumbs];
    } else {
      // 아니면 기본 breadcrumb 생성
      newBreadcrumbs = [{ label: menu, path: '#' }];
      
      if (subMenu) {
        newBreadcrumbs.push({ label: subMenu, path: path || '#' });
      }
    }
    
    setBreadcrumbs(newBreadcrumbs);
    
    // 현재 페이지 타이틀 업데이트
    const matchedPath = getMatchedBasePath(path || '/');
    const routeSettings = routeConfig[matchedPath];
    if (routeSettings) {
      setCurrentTitle(routeSettings.title);
    } else {
      // 현재 페이지 타이틀 (breadcrumb의 마지막 항목)
      const lastBreadcrumb = newBreadcrumbs[newBreadcrumbs.length - 1];
      setCurrentTitle(lastBreadcrumb.label);
    }
  };
  
  // breadcrumb에 새 항목 추가 함수
  const addBreadcrumb = (label, path) => {
    setBreadcrumbs(prev => {
      const newBreadcrumbs = [...prev, { label, path }];
      setCurrentTitle(label);
      return newBreadcrumbs;
    });
  };
  
  // breadcrumb를 특정 인덱스까지 자르는 함수 (이전 단계로 이동 시)
  const truncateBreadcrumbsTo = (index) => {
    setBreadcrumbs(prev => {
      const newBreadcrumbs = prev.slice(0, index + 1);
      const lastBreadcrumb = newBreadcrumbs[newBreadcrumbs.length - 1];
      setCurrentTitle(lastBreadcrumb.label);
      return newBreadcrumbs;
    });
  };
  
  // breadcrumb 완전히 새로 설정하는 함수
  const setBreadcrumbsPath = (newBreadcrumbs) => {
    setBreadcrumbs(newBreadcrumbs);
    
    if (newBreadcrumbs.length > 0) {
      const lastBreadcrumb = newBreadcrumbs[newBreadcrumbs.length - 1];
      setCurrentTitle(lastBreadcrumb.label);
    }
  };

  return (
    <NavigationContext.Provider value={{ 
      activeMenu, 
      activeSubMenu,
      currentTitle,
      breadcrumbs,
      updateNavigation,
      addBreadcrumb,
      truncateBreadcrumbsTo,
      setBreadcrumbsPath
    }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  return useContext(NavigationContext);
}